import React, { useState } from "react";
import styled from "styled-components";
import {
  FaLaptopCode,
  FaMobileAlt,
  FaBrain,
  FaPenNib,
  FaCogs,
  FaArrowRight,
  FaCalendarAlt,
} from "react-icons/fa";

/* ─── data ─── */

const services = [
  {
    icon: <FaLaptopCode />,
    name: "Desarrollo Web",
    description:
      "Desarrollamos sitios web responsivos y autoadministrables que reflejan la identidad de tu marca y optimizan la experiencia del usuario.",
    tags: ["React", "Next.js", "Node.js"],
    color: "#00c8ff",
  },
  {
    icon: <FaMobileAlt />,
    name: "Apps Móviles",
    description:
      "Aumenta el éxito de tu negocio con aplicaciones móviles que faciliten la conexión entre tus clientes y tus servicios.",
    tags: ["iOS", "Android", "Swift"],
    color: "#7c3aed",
  },
  {
    icon: <FaBrain />,
    name: "IA & Machine Learning",
    description:
      "Optimiza tu negocio con soluciones de machine learning e inteligencia artificial que transformen datos en decisiones estratégicas.",
    tags: ["Python", "TensorFlow", "OpenAI"],
    color: "#10b981",
  },
  {
    icon: <FaPenNib />,
    name: "UI & UX Design",
    description:
      "Eleva la satisfacción de tus usuarios optimizando la interacción y la experiencia, haciendo que tus productos sean intuitivos y atractivos.",
    tags: ["Figma", "Prototyping", "Research"],
    color: "#f59e0b",
  },
  {
    icon: <FaCogs />,
    name: "Desarrollo de MVP",
    description:
      "Acelera el lanzamiento de tu idea con un MVP que valide tus hipótesis y te permita obtener feedback real de tus usuarios desde el inicio.",
    tags: ["Lean", "Agile", "Full-Stack"],
    color: "#ef4444",
  },
];

/* ─── styled components ─── */

const Section = styled.section`
  padding: 100px 0;
  background: #0f1123;
  overflow: hidden;

  @media (max-width: 900px) {
    padding: 60px 0;
  }
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 40px;

  @media (max-width: 900px) {
    padding: 0 20px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 64px;
`;

const Tag = styled.span`
  display: inline-block;
  background: rgba(0, 200, 255, 0.08);
  border: 1px solid rgba(0, 200, 255, 0.2);
  color: #00c8ff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  padding: 8px 20px;
  border-radius: 50px;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 2.8em;
  color: #ffffff;
  font-weight: 800;
  letter-spacing: -0.5px;
  margin-bottom: 16px;

  span {
    background: linear-gradient(135deg, #00c8ff, #005bea);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  @media (max-width: 900px) {
    font-size: 2em;
  }
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.45);
  font-size: 1.1em;
  max-width: 520px;
  margin: 0 auto;
  line-height: 1.7;

  @media (max-width: 900px) {
    font-size: 1em;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Card = styled.div.withConfig({
  shouldForwardProp: (prop) => !["accentColor", "isActive"].includes(prop),
})`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  padding: 36px 30px 30px;
  position: relative;
  overflow: hidden;
  cursor: default;
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${({ accentColor }) => accentColor};
    opacity: ${({ isActive }) => (isActive ? 1 : 0)};
    transition: opacity 0.3s ease;
  }

  &::after {
    content: "";
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(
      circle at center,
      ${({ accentColor }) => accentColor}08 0%,
      transparent 60%
    );
    opacity: ${({ isActive }) => (isActive ? 1 : 0)};
    transition: opacity 0.5s ease;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-6px);
    background: rgba(255, 255, 255, 0.05);
    border-color: ${({ accentColor }) => accentColor}30;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3),
      0 0 40px ${({ accentColor }) => accentColor}10;

    &::before {
      opacity: 1;
    }
    &::after {
      opacity: 1;
    }
  }
`;

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const IconBox = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "accentColor",
})`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: ${({ accentColor }) => accentColor}15;
  border: 1px solid ${({ accentColor }) => accentColor}25;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: ${({ accentColor }) => accentColor};
  transition: all 0.3s ease;
  flex-shrink: 0;

  ${Card}:hover & {
    background: ${({ accentColor }) => accentColor}20;
    border-color: ${({ accentColor }) => accentColor}40;
    box-shadow: 0 0 20px ${({ accentColor }) => accentColor}15;
  }
`;

const CardNumber = styled.span`
  font-size: 48px;
  font-weight: 900;
  color: rgba(255, 255, 255, 0.03);
  line-height: 1;
  letter-spacing: -2px;
  user-select: none;
`;

const CardTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 10px;
`;

const CardDesc = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.7;
  margin-bottom: 20px;
`;

const TagsRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: auto;
`;

const TechTag = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== "accentColor",
})`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: ${({ accentColor }) => accentColor};
  background: ${({ accentColor }) => accentColor}10;
  border: 1px solid ${({ accentColor }) => accentColor}20;
  padding: 4px 12px;
  border-radius: 50px;
`;

/* ── bottom CTA ── */

const CtaRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  margin-top: 56px;

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 14px;
  }
`;

const CtaGhost = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.85);
  text-decoration: none;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 16px 32px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.32);
    transform: translateY(-2px);
  }
`;

const CtaButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #00c8ff 0%, #005bea 100%);
  color: #ffffff;
  text-decoration: none;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
  padding: 16px 36px;
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;

  svg {
    transition: transform 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 200, 255, 0.25);
    svg {
      transform: translateX(4px);
    }
  }
`;

/* ─── component ─── */

export const Services = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <Section id="services">
      <Inner>
        <Header>
          <Tag>Lo que hacemos</Tag>
          <Title>
            Nuestros <span>Servicios</span>
          </Title>
          <Subtitle>
            Soluciones tecnológicas de punta para llevar tu negocio al siguiente
            nivel.
          </Subtitle>
        </Header>

        <Grid>
          {services.map((s, i) => (
            <Card
              key={i}
              accentColor={s.color}
              isActive={activeIndex === i}
              onMouseEnter={() => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              <CardTop>
                <IconBox accentColor={s.color}>{s.icon}</IconBox>
                <CardNumber>0{i + 1}</CardNumber>
              </CardTop>
              <CardTitle>{s.name}</CardTitle>
              <CardDesc>{s.description}</CardDesc>
              <TagsRow>
                {s.tags.map((t, j) => (
                  <TechTag key={j} accentColor={s.color}>
                    {t}
                  </TechTag>
                ))}
              </TagsRow>
            </Card>
          ))}
        </Grid>

        <CtaRow>
          <CtaButton href="/call">
            <FaCalendarAlt /> Agenda una llamada
          </CtaButton>
          <CtaGhost onClick={() => scrollTo("contact")}>
            Cotiza tu proyecto <FaArrowRight />
          </CtaGhost>
        </CtaRow>
      </Inner>
    </Section>
  );
};
